import { cn } from "~/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { StarRating } from "~/components/ui/star-rating";

/** Per-slot class overrides, merged over the defaults below. */
export interface CardTextContentReviewClassNames {
  root?: string;
  quote?: string;
  stars?: string;
  reviewer?: string;
  avatar?: string;
  avatarFallback?: string;
  details?: string;
  name?: string;
  subtitle?: string;
}

export interface CardTextContentReviewProps {
  quote: string;
  name: string;
  subtitle: string;
  avatarUrl?: string | null;
  rating?: number;
  compact?: boolean;
  /** Overrides the star color — defaults to StarRating's own neutral default. */
  starColor?: string;
  classNames?: CardTextContentReviewClassNames;
  className?: string;
}

function CardTextContentReview({
  quote,
  name,
  subtitle,
  avatarUrl,
  rating,
  compact,
  starColor,
  classNames = {},
  className,
}: CardTextContentReviewProps) {
  return (
    <div className={cn("flex flex-col", compact ? "gap-4" : "gap-6", classNames.root, className)}>
      <p
        className={cn(
          compact ? "text-text-body text-[20px] leading-snug" : "text-text-body text-xl leading-8",
          classNames.quote,
        )}
      >
        &ldquo;{quote}&rdquo;
      </p>
      <StarRating
        rating={rating}
        compact={compact}
        className={classNames.stars}
        {...(starColor ? { color: starColor } : {})}
      />
      <div className={cn("flex items-center gap-2", classNames.reviewer)}>
        <Avatar className={cn("h-8 w-8", classNames.avatar)}>
          {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
          <AvatarFallback className={cn("text-sm uppercase", classNames.avatarFallback)}>{name.charAt(0)}</AvatarFallback>
        </Avatar>
        <div className={cn("flex flex-col gap-1", classNames.details)}>
          <p
            className={cn(
              compact ? "text-text-body text-base leading-none font-medium" : "text-text-body text-md leading-none font-medium",
              classNames.name,
            )}
          >
            {name}
          </p>
          <p
            className={cn(
              compact ? "text-text-body-secondary text-sm leading-snug" : "text-text-body-secondary text-base",
              classNames.subtitle,
            )}
          >
            {subtitle}
          </p>
        </div>
      </div>
    </div>
  );
}

export { CardTextContentReview };
